import React, { Dispatch, SetStateAction, useEffect, useMemo, useRef } from 'react';
import classes from './TextVariationDragLayer.module.scss';
import textEditorClasses from './../SlateWrapper/SlateWrapper.module.scss';
import draggerClasses from './../DraggableVariation/DraggableVariation.module.scss';
import { useDragLayer, XYCoord } from 'react-dnd';
import { DragVariationItem } from '../DraggableVariation/DraggableVariation.view';
import { Button } from '../../../../components/actions/Button';
import { ButtonColorsEnum } from '../../../../constants/button-colors';
import { IconsEnum } from '../../../../constants/icons';
import classNames from 'classnames';
import { Icon } from '../../../../components/images/Icon/Icon';
import { SlateToolbar } from '../SlateToolbar';
import { createEditorWithPlugins } from '../helpers/createEditorWithPlugins';
import { createEditor } from 'slate';
import { serializeTextForDragAndDrop } from '../helpers/serializer';
import { initialParagraph } from '../../states/initial';
import { getTotalHashes } from '../helpers/getTotalHashes';
import { CustomParagraph } from '../../../../models/slate-editor';
import { DraggableTypesObject } from '../../../../constants/drag-and-drop';

export type TextVariationDragLayerViewProps = {
  //
  setIsDragging: Dispatch<SetStateAction<boolean>>;
  showAddCommentButton: boolean;
  firstCommentText: CustomParagraph[];
  shouldShowHashtagCounter: boolean;
};

const getItemStyles = (
  initialOffset: XYCoord | null,
  currentOffset: XYCoord | null,
  sizes: { width: string; height: string; top: number; left: number }
) => {
  if (!initialOffset || !currentOffset) {
    return {
      display: 'none'
    };
  }

  const { x, y } = currentOffset;
  const { width, height, top, left } = sizes;

  const transform = `translate(${x - left}px, ${y - top}px)`;
  return {
    width,
    height,
    transform,
    WebkitTransform: transform
  };
};

export const TextVariationDragLayerView = ({
  setIsDragging,
  showAddCommentButton,
  firstCommentText,
  shouldShowHashtagCounter
}: TextVariationDragLayerViewProps): JSX.Element => {
  const editor = useMemo(() => createEditorWithPlugins(createEditor()), []);
  const { isDragging, item, initialOffset, currentOffset, itemType } = useDragLayer(monitor => ({
    item: monitor.getItem<DragVariationItem>(),
    itemType: monitor.getItemType(),
    initialOffset: monitor.getInitialSourceClientOffset(),
    currentOffset: monitor.getSourceClientOffset(),
    isDragging: monitor.isDragging()
  }));
  const dragRef = useRef<HTMLDivElement | null>(null);

  const { top, left } = dragRef.current ? dragRef.current.getBoundingClientRect() : { top: 0, left: 0 };
  const { width, height } = item ? item : { width: '100%', height: '100%' };

  const sizes = useMemo(
    () =>
      isDragging
        ? { width: `${width}px`, height: `${height}px`, top, left }
        : { width: '100%', height: '100%', top, left },
    [isDragging, width, height, top, left]
  );

  useEffect(() => {
    if (itemType === DraggableTypesObject.TEXT_VARIATION) {
      setIsDragging(old => (isDragging !== old ? isDragging : old));
    } else if (itemType === null && !isDragging) {
      setIsDragging(false);
    }
  }, [isDragging, setIsDragging, itemType]);

  const text = useMemo(
    () =>
      serializeTextForDragAndDrop(
        item && itemType === DraggableTypesObject.TEXT_VARIATION ? item.text : [initialParagraph()]
      ),
    [item, itemType]
  );

  if (itemType !== DraggableTypesObject.TEXT_VARIATION) return <></>;

  const boxClasses = classNames(
    textEditorClasses.TextEditor,
    textEditorClasses.TextEditorIsDragable,
    textEditorClasses.Dragging
  );

  const [hashtagsInText, hashtagsInFirstComment] = item
    ? getTotalHashes([{ id: item.textId, text: item.text }], firstCommentText)
    : [0, 0];
  const hashtagsCount = hashtagsInText + hashtagsInFirstComment;

  return (
    <div className={classes.TextVariationDragLayer} ref={dragRef}>
      <div style={getItemStyles(initialOffset, currentOffset, sizes)}>
        <div className={classes.PreviewBoxWrapper}>
          <div className={boxClasses}>
            <div>
              <div className={'grid-x align-top'}>
                <div className={'cell shrink'}>
                  <div className={draggerClasses.DraggableVariationHandlerWrapper} ref={dragRef}>
                    <div className={draggerClasses.DraggableVariationHandler}>
                      <Icon name={IconsEnum.dragHandle} size={'medium'} />
                    </div>
                  </div>
                </div>
                <div className={'cell auto'}>
                  <div className={classes.TextContent} dangerouslySetInnerHTML={{ __html: text }} />
                  {/*<SlateEditor text={item.text} id={item.textId} editor={editor} isDragging={isDragging} />*/}
                  <div className={textEditorClasses.RemoveVariationButtonWrapper}>
                    <Button
                      type={'a'}
                      variant={'clear'}
                      size={'large'}
                      color={ButtonColorsEnum.primary}
                      icon={{ side: 'center', value: IconsEnum.trash }}
                      action={() => {
                        /**/
                      }}
                    />
                  </div>
                  <SlateToolbar
                    canDelete={true}
                    variationId={item ? item.textId : 0}
                    addVariation={() => {
                      /**/
                    }}
                    {...(showAddCommentButton
                      ? {
                          addComment: () => {
                            /**/
                          }
                        }
                      : {})}
                    {...(shouldShowHashtagCounter ? { hashtagsCount } : {})}
                    openAiAssistant={() => {
                      /**/
                    }}
                    editor={editor}
                    publishNext={false}
                    comboErrorMessages={[]}
                    comboWarningMessages={[]}
                    errorVariationMessages={[]}
                    warningVariationMessages={[]}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
